import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  IonContent,
  IonPage,
  IonToggle,
} from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { LanguageSwitcher } from '../components/profile/LanguageSwitcher';
import { SUPPORTED_LANGUAGES } from '../i18n/config';
import { ChevronRight } from 'lucide-react';
import { useProfileStore } from '../store/useProfileStore';
import {
  useActiveProfile,
  useUpdateNewDishSnooze,
  useUpdateNotificationSettings,
} from '../hooks/useProfile';
import { useSnoozedChefs } from '../hooks/useNotificationMutes';
import { SnoozedChefsModal } from '../components/profile/SnoozedChefsModal';
import {
  getPushPermissionState,
  isAndroid,
  isIOS,
  isStandalonePWA,
} from '../services/platformService';
import { openPushPermissionModal } from '../services/pushPermissionModalService';
import { openInstallInstructionsModal } from '../services/installInstructionsModalService';
import { canPromptInstall, promptInstall } from '../services/pwaInstallService';
import { openPushBlockedModal } from '../services/pushBlockedModalService';
import { getPushToken, requestPushPermission } from '../services/pushService';
import { setDevicePushToken } from '../services/deviceService';
import { useActivityStore } from '../store/useActivityStore';
import { AppearanceControl } from '../components/ui/AppearanceControl';
import type { NotificationSettings } from '../types/profileSettings';

type Channel = keyof NotificationSettings;

// Channel order is stable; label text comes from profile.json so a
// translator can adjust capitalization or word choice without code changes.
// (Push/Email/SMS are protocol-name literals — translation files repeat
// them by default; a translator can localize if needed.)
const CHANNELS: readonly Channel[] = ['push', 'email', 'sms'] as const;

interface NotificationToggleGroupProps {
  title: string;
  description: string;
  settings: NotificationSettings;
  onToggle: (channel: Channel, value: boolean) => void;
  disabled?: boolean;
  // Monotonic counter bumped every time the user interacts with the push
  // toggle. Used as part of the IonToggle's `key` to force a remount that
  // re-syncs the visual thumb position with the controlled `checked` prop.
  // Without this, IonToggle does an optimistic flip on tap and leaves the
  // thumb visually "on" even when our handler chose not to persist (e.g.
  // user clicks "Maybe later" in the modal, or OS state is denied).
  pushSyncEpoch: number;
  // Whether THIS device can receive push: OS permission granted and its FCM
  // token on the backend. The push toggle shows on only when the account
  // preference is on AND this is true.
  deviceCanPush: boolean;
  // Bound `t` for the profile namespace, threaded through so the channel
  // labels (Push / Email / SMS) come from the locale bundle rather than
  // duplicated literals.
  t: TFunction;
}

const NotificationToggleGroup: React.FC<NotificationToggleGroupProps> = ({
  title,
  description,
  settings,
  onToggle,
  disabled,
  pushSyncEpoch,
  deviceCanPush,
  t,
}) => (
  <div className="rounded-lg border border-border p-4 space-y-1">
    <h3 className="font-medium">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
    <div className="divide-y divide-border">
      {CHANNELS.map((channel) => (
        <div key={channel} className="py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">{t(`channels.${channel}`)}</span>
            <IonToggle
              key={channel === 'push' ? `push-${pushSyncEpoch}` : channel}
              // Push shows on only when this device can receive it. Display
              // only: the saved preference covers all of the user's devices,
              // so a device without permission must not turn it off for the
              // others.
              checked={channel === 'push' ? settings.push && deviceCanPush : settings[channel]}
              onIonChange={(e) => onToggle(channel, e.detail.checked)}
              disabled={disabled}
            />
          </div>
          {channel === 'push' && settings.push && !deviceCanPush && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('notifications.pushNotOnThisDevice')}
            </p>
          )}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Settings & Notifications. Holds the notification preference toggles (with
 * the push OS-permission flow), the appearance/theme switcher, and language
 * selection — all split off the old single ProfilePage.
 */
export const SettingsNotificationsPage: React.FC = () => {
  const { t } = useTranslation('profile');
  // Refetch the active profile on mount so the toggles reflect any BE-side
  // changes the FE didn't initiate (another device, admin edits, auto-flips).
  useActiveProfile();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const queryClient = useQueryClient();
  // Bumped on every tap of the push toggle to force IonToggle to remount
  // and re-sync its visual thumb position with the controlled `checked`
  // prop. IonToggle's optimistic flip-on-tap behavior otherwise leaves the
  // thumb visually "on" when we choose not to persist (modal cancel,
  // denied scroll, token-fetch fail).
  const [pushSyncEpoch, setPushSyncEpoch] = useState(0);
  const updateSettings = useUpdateNotificationSettings();
  const updateNewDishSnooze = useUpdateNewDishSnooze();
  const { data: snoozedChefs } = useSnoozedChefs();
  const [snoozedModalOpen, setSnoozedModalOpen] = useState(false);

  const settings = activeProfile?.metadata?.settings;
  const snoozedCount = snoozedChefs?.length ?? 0;

  // Whether this device can receive push: OS permission granted and the FCM
  // token on the backend. Permission can change in the phone's settings
  // while the app is in the background, so it is re-read whenever the page
  // becomes visible again.
  const [pushPermission, setPushPermission] = useState(getPushPermissionState);
  const pushTokenRegistered = useActivityStore((s) => s.pushTokenRegistered);
  const deviceCanPush = pushPermission === 'granted' && pushTokenRegistered;

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setPushPermission(getPushPermissionState());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // A token registering means permission was just granted (the Enable modal,
  // or the launch-time token arriving after this page opened): re-read it so
  // the toggle reflects the grant without waiting for a visibility change.
  useEffect(() => {
    setPushPermission(getPushPermissionState());
  }, [pushTokenRegistered]);

  // Repair: permission is granted but this device's token never reached the
  // backend (e.g. permission turned back on in the phone's settings). Fetch
  // and send it once in the background, no prompt; the toggle turns on by
  // itself when it lands. Once per permission grant, not on every render.
  const tokenRepairTried = useRef(false);
  useEffect(() => {
    if (pushPermission !== 'granted') {
      tokenRepairTried.current = false;
      return;
    }
    if (pushTokenRegistered || tokenRepairTried.current) return;
    tokenRepairTried.current = true;
    void (async () => {
      const token = await getPushToken();
      if (token) await setDevicePushToken(token);
    })();
  }, [pushPermission, pushTokenRegistered]);

  const contentRef = useRef<HTMLIonContentElement>(null);
  // Stable identity via useCallback — PullToRefresh rebinds its gesture
  // whenever `onRefresh` changes.
  const handleRefresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
    [queryClient],
  );

  /**
   * Mutate one channel's pref on either the notifications or marketing
   * settings block. Returns the promise so callers that need to chain on
   * completion (the push-ON happy path) can await it.
   */
  const persistChannelToggle = (
    category: 'marketing' | 'notifications',
    channel: Channel,
    value: boolean,
  ) => {
    if (!activeProfile || !settings) return;
    const current = category === 'marketing' ? settings.marketing : settings.notifications.profile;
    updateSettings.mutate({
      profileId: activeProfile.id,
      category,
      settings: { ...current, [channel]: value },
    });
  };

  const handleToggle = async (
    category: 'marketing' | 'notifications',
    channel: Channel,
    value: boolean,
  ) => {
    if (!activeProfile || !settings) return;

    // Push needs special handling: persisting the BE pref `true` is only
    // honest if delivery is ACTUALLY possible — i.e. OS permission is
    // granted AND an FCM token is registered on this device's BE row.
    // Otherwise we'd be persisting a pref that silently can't deliver.
    //
    // We never persist `true` preemptively; we only persist `true` AFTER
    // `requestPushPermission()` returns true (which guarantees both the
    // OS-permission grant AND a successful setDevicePushToken call to
    // the BE). Toggling OFF always persists immediately.
    if (channel === 'push') {
      // Force IonToggle to re-sync its visual state with the actual
      // checked prop on the next render. This counters the optimistic
      // flip-on-tap that would otherwise leave the thumb visually "on"
      // in every path below that doesn't immediately persist `true`
      // (modal cancel, blocked-modal open, token-fetch failure). The
      // remount snaps the thumb back to the truthful state.
      setPushSyncEpoch((e) => e + 1);

      if (value === false) {
        // Opt-out: no OS interaction needed.
        persistChannelToggle(category, channel, false);
        return;
      }

      const perm = getPushPermissionState();
      if (perm === 'granted') {
        // OS perm is good, but the FCM token may not actually be on the
        // BE device row (e.g. user cleared site data, token wasn't PUT
        // after a previous grant, etc.). requestPushPermission() is
        // idempotent here: it re-asks (no prompt — already granted),
        // refreshes the FCM token, and PUTs it to the BE. Persist the
        // pref only if all that succeeds. On failure (rare transient
        // FCM/network blip), leave the toggle visually off — the
        // pushSyncEpoch bump above already re-syncs it — and let the
        // user re-toggle when they're ready.
        const ready = await requestPushPermission();
        if (ready) {
          persistChannelToggle(category, channel, true);
        }
        setPushPermission(getPushPermissionState());
        return;
      }

      if (perm === 'default') {
        // Never asked. The modal's Enable button calls
        // requestPushPermission(), and only on success do we flip the BE
        // pref via the onGranted callback. If the user denies the
        // browser prompt or dismisses the modal, the BE pref stays
        // false and the toggle visually stays off.
        openPushPermissionModal({
          onGranted: () => persistChannelToggle(category, channel, true),
        });
        return;
      }

      if (perm === 'denied') {
        // Terminal from the FE's perspective — browser permission was
        // declined and we can't programmatically re-ask. Surface a small
        // modal with platform-specific OS-settings instructions only
        // when the user actively tries to turn push on (we deliberately
        // don't constantly remind them via a persistent row). They can
        // close the modal and continue using the app.
        openPushBlockedModal();
        return;
      }

      // perm === 'unsupported'. iOS Safari tab is the realistic case —
      // open the standard permission modal, which detects the platform
      // and renders the "Add to Home Screen" install instructions. On
      // any non-iOS browser old enough to lack push support, silently
      // skip (toggle stays off, no action available).
      if (isIOS()) {
        openPushPermissionModal();
      }
      return;
    }

    // Non-push channels: persist directly, no OS interaction.
    persistChannelToggle(category, channel, value);
  };

  // Title band hides on scroll-down, returns on scroll-up.
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('menu.settings')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-6">
          {/* Language — also available from the sidebar's top-right flipper. */}
          {SUPPORTED_LANGUAGES.length >= 2 && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h3 className="font-medium">{t('language.title')}</h3>
              <LanguageSwitcher />
            </div>
          )}

          {/* Add to Home Screen — iPhone-on-web only (Android/desktop and
              already-installed users have nothing to install here). Reuses the
              push-permission modal's iOS install-directions variant. */}
          {isIOS() && !isStandalonePWA() && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h3 className="font-medium">{t('actions.installApp')}</h3>
              <p className="text-sm text-muted-foreground">{t('install.description')}</p>
              <button
                type="button"
                onClick={() => openInstallInstructionsModal({ platform: 'ios' })}
                className="flex w-full items-center justify-center rounded-lg btn-outline border-brand-primary py-2 text-sm font-semibold text-brand-primary active:opacity-80"
              >
                {t('install.cta')}
              </button>
            </div>
          )}

          {/* Android install — hybrid: fire Chrome's native one-tap dialog when
              it's armed (beforeinstallprompt captured in pwaInstallService),
              else fall back to the manual ⋮-menu instructions modal. Non-
              Chromium browsers (Firefox/Samsung Internet) always take the
              fallback since they never fire beforeinstallprompt. */}
          {isAndroid() && !isStandalonePWA() && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h3 className="font-medium">{t('actions.installApp')}</h3>
              <p className="text-sm text-muted-foreground">{t('install.description')}</p>
              <button
                type="button"
                onClick={() => {
                  if (canPromptInstall()) {
                    void promptInstall();
                  } else {
                    openInstallInstructionsModal({ platform: 'android' });
                  }
                }}
                className="flex w-full items-center justify-center rounded-lg btn-outline border-brand-primary py-2 text-sm font-semibold text-brand-primary active:opacity-80"
              >
                {t('install.installCta')}
              </button>
            </div>
          )}

          {/* Notification Settings */}
          {settings && (
            <div className="space-y-4">
              <NotificationToggleGroup
                title={t('notifications.title')}
                description={t('notifications.description')}
                settings={settings.notifications.profile}
                onToggle={(channel, value) => handleToggle('notifications', channel, value)}
                disabled={updateSettings.isPending}
                pushSyncEpoch={pushSyncEpoch}
                deviceCanPush={deviceCanPush}
                t={t}
              />
              <NotificationToggleGroup
                title={t('marketing.title')}
                description={t('marketing.description')}
                settings={settings.marketing}
                onToggle={(channel, value) => handleToggle('marketing', channel, value)}
                disabled={updateSettings.isPending}
                pushSyncEpoch={pushSyncEpoch}
                deviceCanPush={deviceCanPush}
                t={t}
              />

              {/* New-dish notifications from chefs you know: a global snooze
                  toggle + a shortcut into the per-chef snoozed list. */}
              <div className="rounded-lg border border-border p-4 space-y-1">
                <h3 className="font-medium">{t('newDishSnooze.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('newDishSnooze.description')}
                </p>
                <div className="divide-y divide-border">
                  <div className="flex items-center justify-between py-3">
                    <span className="text-sm">{t('newDishSnooze.alertsAllLabel')}</span>
                    {/* Presented in positive polarity so this toggle reads the
                        same way as the Push/Email/SMS ones above it: on = you
                        get the alerts. The stored field is the inverse
                        (`snoozeNewDish`), so flip at this boundary only. */}
                    <IonToggle
                      checked={!(settings.notifications.snoozeNewDish ?? false)}
                      disabled={updateNewDishSnooze.isPending || !activeProfile}
                      onIonChange={(e) => {
                        if (!activeProfile) return;
                        updateNewDishSnooze.mutate({
                          profileId: activeProfile.id,
                          snoozeNewDish: !e.detail.checked,
                        });
                      }}
                    />
                  </div>
                  {/* Label/value row, iOS-settings style: the left side is a
                      permanent noun and the right side is the current value.
                      Deliberately always tappable, even at zero — a disabled,
                      dimmed row here reads as "switched off by the toggle
                      above", which is what confused users. The destination
                      carries its own empty state instead. */}
                  <button
                    type="button"
                    onClick={() => setSnoozedModalOpen(true)}
                    className="flex w-full items-center justify-between py-3 text-left"
                  >
                    <span className="text-sm">{t('newDishSnooze.snoozedLabel')}</span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      {snoozedCount > 0 ? snoozedCount : t('newDishSnooze.snoozedValueNone')}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </button>
                </div>
                {/* Section footer: the one fact neither row can state on its
                    own, namely that a per-chef snooze is scoped to that chef
                    and independent of the global alerts toggle. */}
                <p className="pt-1 text-xs text-muted-foreground">
                  {t('newDishSnooze.perChefNote')}
                </p>
              </div>
            </div>
          )}

          {/* Appearance / theme */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <h3 className="font-medium">{t('common:appearance.title')}</h3>
            <AppearanceControl />
          </div>

          {/* Build version. Tiny, muted, far-right — just enough to let
              you (or a teammate testing) confirm which build is running
              on a given device without DevTools. Sourced from package.json
              via the VITE_VERSION define in vite.config.ts. */}
          <p className="text-right text-[10px] text-muted-foreground/60 pt-2 select-text">
            v{import.meta.env.VITE_VERSION}
          </p>
        </div>
        </PullToRefresh>
      </IonContent>

      <SnoozedChefsModal
        isOpen={snoozedModalOpen}
        onClose={() => setSnoozedModalOpen(false)}
      />
    </IonPage>
  );
};
